import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import './ServicePage.css';

const TrainingServices = () => {
  const programs = [
    {
      icon: 'fas fa-cogs',
      title: 'Equipment Operation Training',
      description: 'Comprehensive training on proper equipment operation, maintenance procedures, and troubleshooting.',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      duration: '2-5 Days',
      participants: 'Up to 20'
    },
    {
      icon: 'fas fa-shield-alt',
      title: 'Safety Protocols Training',
      description: 'Essential safety training covering workplace hazards, emergency procedures, and compliance requirements.',
      image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      duration: '1-3 Days',
      participants: 'Up to 30'
    },
    {
      icon: 'fas fa-tools',
      title: 'Maintenance Basics',
      description: 'Hands-on training for preventive maintenance, basic repairs, and equipment care procedures.',
      image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      duration: '3-7 Days',
      participants: 'Up to 15'
    },
    {
      icon: 'fas fa-certificate',
      title: 'Certification Programs',
      description: 'Industry-recognized certification programs for equipment operators and maintenance personnel.',
      image: 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      duration: '1-2 Weeks',
      participants: 'Up to 25'
    }
  ];

  const benefits = [
    {
      icon: 'fas fa-chart-line',
      title: 'Improved Efficiency',
      description: 'Properly trained operators achieve 30% higher productivity rates'
    },
    {
      icon: 'fas fa-shield-check',
      title: 'Enhanced Safety',
      description: 'Reduce workplace accidents by up to 60% with proper training'
    },
    {
      icon: 'fas fa-dollar-sign',
      title: 'Cost Reduction',
      description: 'Lower maintenance costs through proper equipment handling'
    },
    {
      icon: 'fas fa-award',
      title: 'Compliance',
      description: 'Meet industry standards and regulatory requirements'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Needs Assessment',
      description: 'Evaluating your team\'s current skill level and training requirements.',
      icon: 'fas fa-clipboard-check'
    },
    {
      step: '02',
      title: 'Custom Curriculum',
      description: 'Developing tailored training programs specific to your equipment and processes.',
      icon: 'fas fa-book'
    },
    {
      step: '03',
      title: 'Training Delivery',
      description: 'Conducting hands-on training sessions with experienced instructors.',
      icon: 'fas fa-chalkboard-teacher'
    },
    {
      step: '04',
      title: 'Certification & Follow-up',
      description: 'Providing certifications and ongoing support for continuous improvement.',
      icon: 'fas fa-medal'
    }
  ];

  const gallery = [
    'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    'https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    'https://images.unsplash.com/photo-1581092160562-40aa08e78837?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    'https://images.unsplash.com/photo-1611224923853-80b023f02d71?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    'https://images.unsplash.com/photo-1503387762-592deb58ef4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
  ];

  return (
    <div className="service-page">
      {/* Hero Section */}
      <section className="service-hero parallax-bg">
        <div className="hero-background">
          <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" alt="Training Services" />
        </div>
        <div className="parallax-overlay"></div>
        <div className="container-wide">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="hero-content"
            >
              <div className="breadcrumb">
                <Link to="/">Home</Link>
                <span>/</span>
                <Link to="/services">Services</Link>
                <span>/</span>
                <span>Training Services</span>
              </div>
              <h1>Training Sessions</h1>
              <h2>Professional Operator Training Programs</h2>
              <p>Comprehensive training programs for equipment operators ensuring safe, efficient operations and compliance with industry standards.</p>
              <div className="hero-buttons">
                <Link to="/contact" className="btn btn-primary">
                  Get Quote <i className="fas fa-arrow-right"></i>
                </Link>
                <a href="tel:+1234567890" className="btn btn-secondary">
                  Call Now <i className="fas fa-phone"></i>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Overview */}
      <section className="service-overview section">
        <div className="container-wide">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title">Professional Training Excellence</h2>
              <p className="section-subtitle">
                Empowering your workforce with the knowledge and skills needed for optimal performance
              </p>
            </motion.div>

            <div className="overview-content">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="overview-text"
              >
                <h3>Comprehensive Training Solutions</h3>
                <p>
                  Our training programs are designed by industry experts with decades of hands-on experience. 
                  We understand that well-trained operators are the foundation of efficient and safe operations. 
                  Our comprehensive approach ensures your team gains both theoretical knowledge and practical skills.
                </p>
                <p>
                  From basic equipment operation to advanced troubleshooting and maintenance procedures, 
                  our training programs are tailored to meet your specific needs and industry requirements. 
                  We provide both on-site and off-site training options to accommodate your schedule and preferences.
                </p>
                <div className="key-benefits">
                  <div className="benefit-item">
                    <i className="fas fa-check-circle"></i>
                    <span>Hands-on Learning</span>
                  </div>
                  <div className="benefit-item">
                    <i className="fas fa-check-circle"></i>
                    <span>Industry Certification</span>
                  </div>
                  <div className="benefit-item">
                    <i className="fas fa-check-circle"></i>
                    <span>Flexible Scheduling</span>
                  </div>
                  <div className="benefit-item">
                    <i className="fas fa-check-circle"></i>
                    <span>Ongoing Support</span>
                  </div>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="overview-image"
              >
                <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Training Services Overview" />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Training Programs */}
      <section className="service-features section parallax-bg">
        <div className="hero-background">
          <img src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" alt="Programs Background" />
        </div>
        <div className="parallax-overlay"></div>
        <div className="container-wide">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title">Training Programs</h2>
              <p className="section-subtitle">
                Specialized training programs designed for different skill levels and requirements
              </p>
            </motion.div>

            <div className="programs-grid">
              {programs.map((program, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="program-card card-hover-lift card-glow"
                  whileHover={{ y: -10 }}
                >
                  <div className="program-image">
                    <img src={program.image} alt={program.title} />
                    <div className="program-overlay">
                      <i className={program.icon}></i>
                    </div>
                  </div>
                  <div className="program-content">
                    <h3>{program.title}</h3>
                    <p>{program.description}</p>
                    <div className="program-details">
                      <div className="detail-item">
                        <i className="fas fa-clock"></i>
                        <span>Duration: {program.duration}</span>
                      </div>
                      <div className="detail-item">
                        <i className="fas fa-users"></i>
                        <span>Max: {program.participants}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="service-overview section">
        <div className="container-wide">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title">Training Benefits</h2>
              <p className="section-subtitle">
                Measurable improvements in safety, efficiency, and compliance
              </p>
            </motion.div>

            <div className="benefits-grid">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="benefit-card card-hover-lift"
                  whileHover={{ y: -10 }}
                >
                  <div className="benefit-icon">
                    <i className={benefit.icon}></i>
                  </div>
                  <h3>{benefit.title}</h3>
                  <p>{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="service-process section">
        <div className="container-wide">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title">Training Process</h2>
              <p className="section-subtitle">
                Our structured approach to delivering effective training programs
              </p>
            </motion.div>

            <div className="process-timeline">
              {process.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="process-step"
                >
                  <div className="step-number">{step.step}</div>
                  <div className="step-icon">
                    <i className={step.icon}></i>
                  </div>
                  <div className="step-content">
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="service-gallery section parallax-bg">
        <div className="hero-background">
          <img src="https://images.unsplash.com/photo-1581092160562-40aa08e78837?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" alt="Gallery Background" />
        </div>
        <div className="parallax-overlay"></div>
        <div className="container-wide">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title">Training Gallery</h2>
              <p className="section-subtitle">
                See our training programs in action across various facilities
              </p>
            </motion.div>

            <div className="gallery-grid">
              {gallery.map((image, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="gallery-item"
                  whileHover={{ scale: 1.05 }}
                >
                  <img src={image} alt={`Training ${index + 1}`} />
                  <div className="gallery-overlay">
                    <i className="fas fa-search-plus"></i>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="service-cta section">
        <div className="container-wide">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="cta-content"
            >
              <h2>Ready to Train Your Team?</h2>
              <p>Invest in your workforce with our professional training programs</p>
              <div className="cta-buttons">
                <Link to="/contact" className="btn btn-primary">
                  Schedule Training
                </Link>
                <Link to="/services" className="btn btn-secondary">
                  View All Services
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrainingServices;